import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressOverview = () => {
  const overallProgress = 72;
  const phases = [
    {
      id: 1,
      name: 'Phase 1: Foundation',
      status: 'completed',
      progress: 100,
      features: 12,
      startDate: '2024-06-01',
      endDate: '2024-08-31',
      color: 'success'
    },
    {
      id: 2,
      name: 'Phase 2: Core Features',
      status: 'in_progress',
      progress: 75,
      features: 8,
      startDate: '2024-09-01',
      endDate: '2024-10-31',
      color: 'warning'
    },
    {
      id: 3,
      name: 'Phase 3: Advanced Features',
      status: 'planned',
      progress: 25,
      features: 15,
      startDate: '2024-11-01',
      endDate: '2024-12-31',
      color: 'accent'
    },
    {
      id: 4,
      name: 'Phase 4: Optimization',
      status: 'planned',
      progress: 0,
      features: 7,
      startDate: '2025-01-01',
      endDate: '2025-02-28',
      color: 'muted'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle2';
      case 'in_progress': return 'Clock';
      case 'planned': return 'Calendar';
      default: return 'Circle';
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'accent': return 'text-accent bg-accent/10 border-accent/20';
      case 'muted': return 'text-muted-foreground bg-muted/10 border-border';
      default: return 'text-foreground bg-card border-border';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-1">
            Vue d'Ensemble du Projet
          </h3>
          <p className="text-sm text-muted-foreground">
            Progression générale et phases de développement
          </p>
        </div>
        
        {/* Overall Progress Circle */}
        <div className="relative w-20 h-20">
          <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
            {/* Background circle */}
            <path
              className="text-muted stroke-current"
              strokeWidth="3"
              fill="transparent"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            {/* Progress circle */}
            <path
              className="text-accent stroke-current"
              strokeWidth="3"
              strokeLinecap="round"
              fill="transparent"
              strokeDasharray={`${overallProgress}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-sm font-bold text-foreground">{overallProgress}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Phases Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {phases?.map((phase) => (
          <div
            key={phase?.id}
            className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${getColorClasses(phase?.color)}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name={getStatusIcon(phase?.status)} size={18} />
                <span className="text-sm font-medium">Phase {phase?.id}</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold">{phase?.progress}%</div>
              </div>
            </div>

            <h4 className="font-medium text-sm mb-2 leading-tight">
              {phase?.name}
            </h4>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="w-full bg-background/50 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    phase?.color === 'success' ? 'bg-success' :
                    phase?.color === 'warning' ? 'bg-warning' :
                    phase?.color === 'accent' ? 'bg-accent' : 'bg-muted-foreground'
                  }`}
                  style={{ width: `${phase?.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-1 text-xs opacity-80">
              <div className="flex items-center space-x-1">
                <Icon name="Layers" size={12} />
                <span>{phase?.features} fonctionnalités</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={12} />
                <span>{formatDate(phase?.startDate)} - {formatDate(phase?.endDate)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-success mb-1">18</div>
            <p className="text-xs text-muted-foreground">Fonctionnalités Livrées</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning mb-1">12</div>
            <p className="text-xs text-muted-foreground">En Développement</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent mb-1">24</div>
            <p className="text-xs text-muted-foreground">Planifiées</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground mb-1">3</div>
            <p className="text-xs text-muted-foreground">Équipes Actives</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressOverview;